import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, LogOut } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

export const LOGO_SRC = '/logo.png';

const NAV_LINKS = [
  { name: 'Erstberatung', href: '/erstberatung' },
  { name: 'Lösungen', href: '/loesungen' },
  { name: 'Insights', href: '/insights' },
  { name: 'Über Uns', href: '/ueber-uns' },
];

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin, logout } = useAuth();

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const handleLogout = () => {
    logout();
    setMobileMenuOpen(false);
    navigate('/');
  };

  const closeMenu = () => setMobileMenuOpen(false);

  const ctaButtons = (mobile = false) => {
    const block = mobile ? 'btn--block' : '';
    return isAuthenticated ? (
      <>
        <Link to={isAdmin ? '/admin' : '/portal'} className={`btn btn--purple btn--sm ${block}`} onClick={closeMenu} data-testid={mobile ? 'mobile-portal-btn' : 'portal-btn'}>
          {isAdmin ? 'Admin' : 'Kundenportal'}
        </Link>
        <button type="button" onClick={handleLogout} className={`btn btn--ghost btn--sm ${block}`} data-testid={mobile ? 'mobile-logout-btn' : 'logout-btn'}>
          <LogOut className="w-4 h-4" />
          Logout
        </button>
      </>
    ) : (
      <>
        <Link to="/login" className={`btn btn--ghost btn--sm ${block}`} onClick={closeMenu} data-testid={mobile ? 'mobile-login-btn' : 'login-btn'}>
          Kundenlogin
        </Link>
        <Link to="/register" className={`btn btn--purple btn--sm ${block}`} onClick={closeMenu} data-testid={mobile ? 'mobile-platform-btn' : 'platform-btn'}>
          Plattform entdecken
        </Link>
      </>
    );
  };

  return (
    <header className="sticky top-0 z-50 bg-off border-b-2 border-ink" data-testid="main-header">
      <div className="wrap flex items-center gap-6 py-3 md:py-4">
        <Link to="/" className="flex items-center flex-shrink-0 mr-auto" data-testid="logo-link" aria-label="gastro-beratung.de – Startseite">
          <img src={LOGO_SRC} alt="gastro-beratung.de" width="600" height="108" className="h-8 sm:h-9 md:h-10 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-7" data-testid="desktop-nav" aria-label="Hauptnavigation">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.name}
              to={link.href}
              className={`font-medium text-base pb-0.5 border-b-2 transition-colors hover:text-purple hover:border-purple whitespace-nowrap ${
                isActive(link.href) ? 'text-purple border-purple' : 'border-transparent text-ink'
              }`}
              data-testid={`nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
            >
              {link.name}
            </Link>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
          {ctaButtons(false)}
        </div>

        <button
          type="button"
          className="lg:hidden inline-flex items-center justify-center w-11 h-11 border-2 border-ink bg-off"
          onClick={() => setMobileMenuOpen((o) => !o)}
          aria-expanded={mobileMenuOpen}
          aria-label={mobileMenuOpen ? 'Menü schließen' : 'Menü öffnen'}
          data-testid="mobile-menu-btn"
        >
          {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="lg:hidden border-t-2 border-ink bg-off" data-testid="mobile-menu">
          <nav className="wrap py-4 flex flex-col" aria-label="Mobile Navigation">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.name}
                to={link.href}
                className={`font-display font-bold text-xl py-3 border-b border-ink/15 ${isActive(link.href) ? 'text-purple' : 'text-ink'}`}
                onClick={closeMenu}
                data-testid={`mobile-nav-${link.name.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {link.name}
              </Link>
            ))}
            <div className="flex flex-col gap-4 pt-6 pb-2">
              {ctaButtons(true)}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
